import React, { useState, useEffect } from "react";
import {
  Button,
  FormControl,
  TextField,
  Stack,
  InputAdornment,
  Typography,
  Slider,
  Box,
  Paper,
} from "@mui/material";
import OpacityIcon from "@mui/icons-material/Opacity";
import WaterDropIcon from "@mui/icons-material/WaterDrop";

export default function ChooseWaterForm({ task, onSubmit }) {
  const [quantity, setQuantity] = useState(task.quantity || "");
  const [error, setError] = useState("");
  const [touched, setTouched] = useState(false);

  const minQuantity = 10;
  const maxQuantity = 500;

  // Validate form as user types after field has been touched
  useEffect(() => {
    if (touched) {
      validateQuantity(quantity);
    }
  }, [quantity, touched]);

  const handleSubmit = (event) => {
    event.preventDefault();
    setTouched(true);
    
    if (validateQuantity(quantity)) {
      task.args = `Water - ${quantity}ml`;
      task.quantity = quantity;
      task.item_name = "Water";
      
      onSubmit(task);
    }
  };

  const validateQuantity = (value) => {
    if (!value) {
      setError("Please enter a quantity");
      return false;
    }
    
    const numValue = Number(value);
    
    if (isNaN(numValue)) {
      setError("Please enter a valid number");
      return false;
    }
    
    if (numValue < minQuantity) {
      setError(`Minimum quantity is ${minQuantity} ml`);
      return false;
    }
    
    if (numValue > maxQuantity) {
      setError(`Maximum quantity is ${maxQuantity} ml`);
      return false;
    }
    
    setError("");
    return true;
  };

  const handleSliderChange = (_, newValue) => {
    setQuantity(newValue);
    setTouched(true);
  };

  // Calculate water fill percentage for visualization
  const waterFillPercentage = Math.min(
    Math.max(((quantity || 0) - minQuantity) / (maxQuantity - minQuantity), 0),
    1
  ) * 100;

  return (
    <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
      <form onSubmit={handleSubmit}>
        <Stack spacing={3}>
          <Box display="flex" alignItems="center" gap={1}>
            <OpacityIcon color="primary" />
            <Typography variant="h5" color="primary" fontWeight="medium">
              Select Water Amount
            </Typography>
          </Box>
          
          {/* Water visualization */}
          <Box
            sx={{
              position: "relative",
              height: 120,
              border: "1px solid #e0e0e0",
              borderRadius: 2,
              overflow: "hidden",
              mt: 2
            }}
          >
            <Box
              sx={{
                position: "absolute",
                bottom: 0,
                width: "100%",
                height: `${waterFillPercentage}%`,
                backgroundColor: "primary.light",
                transition: "height 0.3s ease-in-out",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <WaterDropIcon sx={{ fontSize: 40, color: "primary.dark", opacity: 0.7 }} />
            </Box>
            <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)" }}>
              <Typography variant="h4" fontWeight="bold">
                {quantity || 0} ml
              </Typography>
            </Box>
          </Box>

          <Box sx={{ px: 1 }}>
            <Slider
              value={Number(quantity) || 0}
              onChange={handleSliderChange}
              valueLabelDisplay="auto"
              step={10}
              marks={[
                { value: minQuantity, label: `${minQuantity} ml` },
                { value: maxQuantity, label: `${maxQuantity} ml` },
              ]}
              min={minQuantity}
              max={maxQuantity}
            />
          </Box>

          <FormControl fullWidth variant="outlined">
            <TextField
              label="Water Quantity (ml)"
              type="number"
              value={quantity}
              onChange={(e) => {
                setQuantity(e.target.value);
                setTouched(true);
              }}
              error={!!error}
              helperText={error || `Select between ${minQuantity} and ${maxQuantity} ml`}
              InputProps={{
                endAdornment: <InputAdornment position="end">ml</InputAdornment>,
                inputProps: { min: minQuantity, max: maxQuantity }
              }}
            />
          </FormControl>

          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            startIcon={<OpacityIcon />}
            sx={{ 
              py: 1.5,
              borderRadius: 2,
              background: "linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)",
            }}
          >
            Confirm Selection
          </Button>
        </Stack>
      </form>
    </Paper>
  );
}
